/**
 * Shared league-member helpers for client UI and Cloud Functions:
 * rank sentinels (`worldPopulation`), `teamHasList` / `memberHasList`, competing counts.
 */
import { worldPopulation } from '../../constants';
import type { TeamSelectionRecord, leagueMember } from '../../models/user';
import type League from '../../models/league';

/** Rank not yet assigned (unset or worldPopulation sentinel stored for Firestore sort order). */
export function isPendingLeagueRank(currentRank: number | undefined | null): boolean {
  if (currentRank == null) return true;
  const rank = Number(currentRank);
  return rank <= 0 || rank >= worldPopulation;
}

/** Rank assigned by end-of-matchday scoring (1 … N), safe to show in the UI. */
export function isSettledLeagueRank(currentRank: number | undefined | null): boolean {
  if (currentRank == null) return false;
  const rank = Number(currentRank);
  return rank > 0 && rank < worldPopulation;
}

/** True when the user has submitted a full 10-team list for the active tournament. */
export function teamHasList(team: TeamSelectionRecord | undefined | null): boolean {
  return (team?.countryList?.length ?? 0) === 10;
}

/** Reads hasList from a league member doc, with fallback for docs not yet migrated. */
export function memberHasList(member: leagueMember): boolean {
  if (member.hasList !== undefined) {
    return member.hasList;
  }
  return !(member.spectator ?? true);
}

/** Total joined members (including spectators without a list). */
export function getMemberCount(league: League): number {
  return league.memberCount ?? 0;
}

/** Members with a complete list — visible on the league leaderboard. */
export function isLeaderboardMember(member: leagueMember): boolean {
  return memberHasList(member);
}

/** Count shown in the league header and used for leaderboard pagination. */
export function getCompetingCount(league: League): number {
  return league.competingCount ?? 0;
}

/** Change in competingCount when a member's hasList flag transitions. */
export function competingCountDelta(
  beforeHasList: boolean | undefined,
  afterHasList: boolean | undefined,
): number {
  const before = beforeHasList ?? false;
  const after = afterHasList ?? false;
  if (before === after) return 0;
  return after ? 1 : -1;
}

/** Count list-holding members from an in-memory collection (backfill, demos). */
export function countLeaderboardMembers(members: Iterable<leagueMember>): number {
  let count = 0;
  for (const member of members) {
    if (memberHasList(member)) count++;
  }
  return count;
}

/** Zero-based index of the last leaderboard page for a given competing count and page size. */
export function getLastLeaderboardPageIndex(competingCount: number, pageSize: number): number {
  return Math.max(0, Math.ceil(competingCount / pageSize) - 1);
}

/** Join-order index (0-based) for rank-change display before tournament rankings. */
export function getListPosition(
  _league: League,
  member: leagueMember | undefined,
  _uid: string,
): number {
  if (member?.listPosition !== undefined && member.listPosition > 0) {
    return member.listPosition - 1;
  }
  return -1;
}

/** Join-order rank (1-based) for display when currentRank is not set. */
export function getJoinOrderRank(
  league: League,
  member: leagueMember | undefined,
  uid: string,
): number {
  const position = getListPosition(league, member, uid);
  return position >= 0 ? position + 1 : 1;
}

export function memberFieldsFromTeam(
  team: TeamSelectionRecord | undefined | null,
  options: { tournamentStarted: boolean },
): Partial<leagueMember> {
  const hasList = teamHasList(team);
  const patch: Partial<leagueMember> = {
    hasList,
    latecomer: team?.latecomer ?? team?.isLate ?? false,
    listVisibleInLeagues: team?.listVisibleInLeagues ?? false,
  };
  if (!options.tournamentStarted) {
    patch.currentScore = 0;
    patch.prevScore = 0;
    patch.currentRank = -1;
    patch.prevRank = -1;
  }
  return patch;
}
